import {
  type AppearanceVariant,
  type ChromeTheme,
  CROWDEX_CHROME,
  chromeTokens,
  normalizeChromeTheme,
  sameChromeTheme,
} from '@crowdex/ui/lib/appearance/chrome-theme';

/** The app's own code theme: One Light and One Dark Pro, with the default chrome. */
export const CROWDEX_CODE_THEME = 'crowdex';

/**
 * How one variant (light or dark) looks: the code theme the user picked and the chrome it gave
 * the app, which the user may then adjust.
 */
export type VariantAppearance = Readonly<{ codeTheme: string; chrome: ChromeTheme }>;
export type Appearance = Readonly<Record<AppearanceVariant, VariantAppearance>>;

export const DEFAULT_APPEARANCE: Appearance = {
  light: { codeTheme: CROWDEX_CODE_THEME, chrome: CROWDEX_CHROME.light },
  dark: { codeTheme: CROWDEX_CODE_THEME, chrome: CROWDEX_CHROME.dark },
};

// Long enough for `plugin:<name>/<id>` at the manifest's maxima (96 and 64 characters).
const CODE_THEME_ID = /^[a-z0-9][a-z0-9._:/-]{0,199}$/i;

function normalizeCodeThemeId(value: unknown): string {
  return typeof value === 'string' && CODE_THEME_ID.test(value) ? value : CROWDEX_CODE_THEME;
}

function normalizeVariant(input: unknown, variant: AppearanceVariant): VariantAppearance {
  const source = (typeof input === 'object' && input !== null ? input : {}) as Record<
    string,
    unknown
  >;
  return {
    codeTheme: normalizeCodeThemeId(source['codeTheme']),
    chrome: normalizeChromeTheme(source['chrome'], variant),
  };
}

/** Stored appearance settings, every invalid part replaced by its default. */
export function normalizeAppearance(input: unknown): Appearance {
  const source = (typeof input === 'object' && input !== null ? input : {}) as Record<
    string,
    unknown
  >;
  return {
    light: normalizeVariant(source['light'], 'light'),
    dark: normalizeVariant(source['dark'], 'dark'),
  };
}

/** Whether the variant shows the app's own palette, which `theme.css` already defines. */
export function isDefaultChrome(appearance: Appearance, variant: AppearanceVariant): boolean {
  return sameChromeTheme(appearance[variant].chrome, CROWDEX_CHROME[variant]);
}

/**
 * The stylesheet a custom chrome needs. Each variant scopes its tokens to its own `data-theme`,
 * so the light and the dark appearance both stay ready and the theme switch keeps working. The
 * selector outranks `theme.css` (`[data-theme='desktop-dark']`) without `!important`.
 */
export function appearanceCss(appearance: Appearance): string {
  const rules: string[] = [];
  for (const variant of ['light', 'dark'] as const) {
    if (isDefaultChrome(appearance, variant)) continue;
    const declarations = Object.entries(chromeTokens(appearance[variant].chrome, variant))
      .map(([name, value]) => `  ${name}: ${value};`)
      .join('\n');
    rules.push(`[data-theme='desktop-${variant}'][data-theme] {\n${declarations}\n}`);
  }
  return rules.join('\n');
}

const STYLE_ID = 'crowdex-appearance';

/**
 * Applies the chrome. The style element is replaced rather than edited, so watchers of
 * `<head>` (isolated plugin views re-reading the theme) see the change.
 */
export function applyAppearance(appearance: Appearance): void {
  if (typeof document === 'undefined') return;
  const css = appearanceCss(appearance);
  const current = document.getElementById(STYLE_ID);
  if ((current?.textContent ?? '') === css) return;
  current?.remove();
  if (!css) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent = css;
  document.head.append(style);
}

const SHARE_PREFIX = 'crowdex-theme-v1:';

/**
 * A variant's appearance as a string to paste on another device. A theme imported on this device
 * exists nowhere else, so its VS Code data travels with it.
 */
export function exportAppearance(
  value: VariantAppearance,
  variant: AppearanceVariant,
  importedTheme: unknown = null,
): string {
  return `${SHARE_PREFIX}${JSON.stringify({
    variant,
    codeTheme: value.codeTheme,
    chrome: value.chrome,
    ...(importedTheme === null ? {} : { importedTheme }),
  })}`;
}

/**
 * The variant and appearance a share string carries, and the imported theme it brings, to keep
 * on this device first. Throws when it is not one.
 */
export function importAppearance(text: string): {
  variant: AppearanceVariant;
  value: VariantAppearance;
  importedTheme: unknown;
} {
  const trimmed = text.trim();
  if (!trimmed.startsWith(SHARE_PREFIX)) throw new Error('This is not a Crowdex theme.');
  const parsed: unknown = JSON.parse(trimmed.slice(SHARE_PREFIX.length));
  const source = (typeof parsed === 'object' && parsed !== null ? parsed : {}) as Record<
    string,
    unknown
  >;
  const variant = source['variant'];
  if (variant !== 'light' && variant !== 'dark') throw new Error('The theme names no variant.');
  return {
    variant,
    value: normalizeVariant(source, variant),
    importedTheme: source['importedTheme'] ?? null,
  };
}
